import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  createContext,
} from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import store from "./redux";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { register } from "./serviceWorkerRegistration";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

import { ThemeProvider } from "@mui/material/styles";
import { buildAppTheme } from "./theme/appTheme";
import CssBaseline from "@mui/material/CssBaseline";
import { toast } from "react-toastify";

// 👇 Context to allow toggling theme anywhere
export const ThemeModeContext = createContext({
  mode: "light",
  toggleMode: () => {},
});

// 👇 Utility to load saved mode or fallback to system preference
function getInitialMode() {
  const saved = localStorage.getItem("mui-mode");
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function Root() {
  const [mode, setMode] = useState(getInitialMode);

  // 👇 Save to localStorage when mode changes
  useEffect(() => {
    localStorage.setItem("mui-mode", mode);
  }, [mode]);

  const toggleMode = useCallback(
    () => setMode((m) => (m === "light" ? "dark" : "light")),
    []
  );

  const theme = useMemo(() => buildAppTheme(mode), [mode]);

  return (
    <Provider store={store}>
      <BrowserRouter>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <ThemeModeContext.Provider value={{ mode, toggleMode }}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <App />
            </ThemeProvider>
          </ThemeModeContext.Provider>
        </LocalizationProvider>
      </BrowserRouter>
    </Provider>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<Root />);

reportWebVitals();
register();

// Ensure every toast.error call has a toastId unless explicitly provided.
// This avoids duplicate toasts across many call sites without editing each file.
// We respect any existing `toastId` provided by the caller (e.g., axios interceptor).
try {
  const _origError = toast.error.bind(toast);
  function sanitizeId(s) {
    return String(s)
      .slice(0, 60)
      .replace(/\s+/g, "-")
      .replace(/[^a-zA-Z0-9-_.]/g, "")
      .toLowerCase();
  }
  toast.error = (content, options) => {
    const opts = options ? { ...options } : {};
    if (!opts.toastId) {
      if (typeof content === "string") {
        const id = sanitizeId(content);
        opts.toastId = id ? `msg-${id}` : `auto-${Math.random().toString(36).slice(2, 9)}`;
      } else {
        // derive caller location from stack for a stable per-callsite id
        try {
          const stack = new Error().stack || "";
          const lines = stack.split("\n").map((l) => l.trim()).filter(Boolean);
          // caller is usually the 3rd line (0:Error,1:this fn,2:caller)
          const callerLine = lines[2] || lines[1] || "";
          const m = callerLine.match(/(?:\(|@)?([^():]+:\d+:\d+)(?:\))?$/);
          if (m && m[1]) {
            const parts = m[1].split(":");
            const file = parts[0].split("/").slice(-2).join("-");
            const line = parts[1];
            opts.toastId = `loc-${sanitizeId(file)}-l${line}`;
          } else {
            opts.toastId = `auto-${Math.random().toString(36).slice(2, 9)}`;
          }
        } catch (e) {
          opts.toastId = `auto-${Math.random().toString(36).slice(2, 9)}`;
        }
      }
    }
    return _origError(content, opts);
  };
} catch (e) {
  // noop — if toast isn't available yet, skip patching
}
